import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { POST_USER_DATA } from "../../store/action";
import { t } from "../../translations";
import { UPDATE } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";
import { openNotificationWithIcon } from "../functions/notification";

import { createLogger } from "../../utils/logger";
import "../styles/cooking-delivery-day.css";

const logger = createLogger("CookingDeliveryDay");
const CookingDeliveryDay = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  let user = useSelector((state) => state.user);
  let lang = useSelector((state) => state.lang);

  const post_user_data = (data) => {
    dispatch(post_user(data));
  };

  const [load, setLoad] = useState(false);
  const [availabilityMode, setAvailabilityMode] = useState("everyday"); // "everyday" or "somedays"
  const [selectedDays, setSelectedDays] = useState(user?.available_days || []);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState(
    user?.time_slots || []
  );

  const daysOfWeek = [
    { key: "Sun", label: t("select_profil.sunday", lang) },
    { key: "Mon", label: t("select_profil.monday", lang) },
    { key: "Tue", label: t("select_profil.tuesday", lang) },
    { key: "Wed", label: t("select_profil.wednesday", lang) },
    { key: "Thu", label: t("select_profil.thursday", lang) },
    { key: "Fri", label: t("select_profil.friday", lang) },
    { key: "Sat", label: t("select_profil.saturday", lang) }
  ];

  const timeSlots = [
    { key: "7:00AM - 9:00AM", label: "7:00AM - 9:00AM" },
    { key: "3:00PM - 5:00PM", label: "3:00PM - 5:00PM" },
    { key: "4:00PM - 5:00PM", label: "4:00PM - 5:00PM" },
    { key: "7:00PM - 9:00PM", label: "7:00PM - 9:00PM" }
  ];

  useEffect(() => {
    // Initialize state based on user data
    if (user?.available_days && user.available_days.length > 0) {
      setAvailabilityMode("somedays");
      setSelectedDays(user.available_days);
    }
    if (user?.time_slots && user.time_slots.length > 0) {
      setSelectedTimeSlots(user.time_slots);
    }
  }, [user]);

  const handleDayToggle = (dayKey) => {
    if (availabilityMode === "everyday") return;

    const newSelectedDays = selectedDays.includes(dayKey)
      ? selectedDays.filter((day) => day !== dayKey)
      : [...selectedDays, dayKey];

    setSelectedDays(newSelectedDays);
  };

  const handleTimeSlotToggle = (timeSlot) => {
    const newSelectedTimeSlots = selectedTimeSlots.includes(timeSlot)
      ? selectedTimeSlots.filter((slot) => slot !== timeSlot)
      : [...selectedTimeSlots, timeSlot];

    setSelectedTimeSlots(newSelectedTimeSlots);
  };

  const handleAvailabilityModeChange = (mode) => {
    setAvailabilityMode(mode);
    if (mode === "everyday") {
      setSelectedDays(daysOfWeek.map((day) => day.key));
    } else {
      setSelectedDays([]);
    }
  };

  const saveUpdate = async () => {
    if (load) return;

    setLoad(true);

    const request_details = {
      type: UPDATE,
      url: service_api,
      route: `user/update-user/${user?._id}`,
      data: {
        availability:
          availabilityMode === "everyday" ? "every-day" : "few-days",
        available_days:
          availabilityMode === "everyday"
            ? daysOfWeek.map((day) => day.key)
            : selectedDays,
        time_slots: selectedTimeSlots
      },
      token: user?.refresh_token
    };

    try {
      const response = await MakeRequestAsync(request_details);
      const decryptedData = decryptSingleData(response.data.user);
      post_user_data({
        ...decryptedData
      });
      openNotificationWithIcon("success", t("success.profile_updated", lang));
      history.push("/chef/iam-a-chef/home");
    } catch (err) {
      setLoad(false);
      logger.log("We couldn't fetch data: ", err?.response?.data?.message);
      openNotificationWithIcon("error", t("error.profile_update_failed", lang));
    }
  };

  return (
    <div className="cooking-delivery-day-container">
      <div className="cooking-delivery-day-header">
        <h1 className="page-title">{t("cooking_delivery_day.title", lang)}</h1>
      </div>

      <div className="cooking-delivery-day-content">
        <div className="availability-section">
          <p className="section-description">
            {t("cooking_delivery_day.select_days_prompt", lang)}
          </p>

          <div className="availability-toggle">
            <button
              className={`toggle-option ${availabilityMode === "everyday" ? "active" : ""}`}
              onClick={() => handleAvailabilityModeChange("everyday")}
            >
              {t("cooking_delivery_day.every_day", lang)}
            </button>
            <button
              className={`toggle-option ${availabilityMode === "somedays" ? "active" : ""}`}
              onClick={() => handleAvailabilityModeChange("somedays")}
            >
              {t("cooking_delivery_day.some_days", lang)}
            </button>
          </div>

          {availabilityMode === "somedays" && (
            <div className="days-selection">
              {daysOfWeek.map((day) => (
                <button
                  key={day.key}
                  className={`day-button ${selectedDays.includes(day.key) ? "selected" : ""}`}
                  onClick={() => handleDayToggle(day.key)}
                >
                  {day.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="time-slots-section">
          <h3 className="section-title">
            {t("cooking_delivery_day.time_slots", lang)}
          </h3>
          <div className="time-slots-grid">
            {timeSlots.map((slot) => (
              <button
                key={slot.key}
                className={`time-slot-button ${selectedTimeSlots.includes(slot.key) ? "selected" : ""}`}
                onClick={() => handleTimeSlotToggle(slot.key)}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>

        <button className="submit-button" onClick={saveUpdate} disabled={load}>
          {load ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("cooking_delivery_day.submit", lang)
          )}
        </button>
      </div>
    </div>
  );
};

export default CookingDeliveryDay;

const post_user = (data) => ({
  type: POST_USER_DATA,
  data: data
});
